import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageGroupDialog, {
  type GroupPickerUnit,
} from "../components/SignageGroupDialog";
import SignageGroupDeleteDialog from "../components/SignageGroupDeleteDialog";
import SignageGroupContentDialog from "../components/SignageGroupContentDialog";
import SignageChangeChannelDialog from "../components/SignageChangeChannelDialog";
import { tokens } from "../theme";
import { describeError, useToast } from "../components/toast";
import { signageSaveErrorText } from "../utils/signageRefusal";
import { useFormat } from "../i18n/format";
import { foyerApi, type SignageScopeRef } from "../http/foyer";
import type {
  SignageAssignment,
  SignageAssignmentPatch,
  SignageGroupFollowing,
  SignagePlaylist,
  SignageScreenGroup,
} from "@shared/types/signage";
import { PermissionGate } from "../auth/access";

/** What a masked field is called where an operator can see it. */
const FIELD_KEYS = {
  weekly: "signageGroupsPage.fields.weekly",
  defaultPlaylistId: "signageGroupsPage.fields.defaultPlaylistId",
  override: "signageGroupsPage.fields.override",
  soundEnabled: "signageGroupsPage.fields.soundEnabled",
  breaksPerHour: "signageGroupsPage.fields.breaksPerHour",
  breakSeconds: "signageGroupsPage.fields.breakSeconds",
  orientation: "signageGroupsPage.fields.orientation",
} as const;

const REASON_SEPARATOR = " — ";

/**
 * "3 of 8 not following", with the reason behind it.
 *
 * ## Why this is a warning and not an error
 *
 * Nothing here is broken. A screen set by hand outranking its group is the
 * layer model working exactly as designed, and an operator who deliberately
 * took one wall over does not want a red row about it. What they cannot see
 * without this is that the group's promise is not being kept everywhere - so
 * the count states the fact and the tooltip names the cause, and neither
 * suggests a fix.
 *
 * ## Why it counts screens but explains per field
 *
 * The count is the thing you scan a table for; the detail is the thing you open
 * when it surprises you. A screen can take the group's channel and ignore its
 * sound, so it appears once in the count and twice in the reasons.
 */
function FollowingNote({ row }: { row: SignageGroupFollowing | undefined }) {
  const { t } = useTranslation(["signage", "common"]);
  // Undefined means "not answered yet" - deliberately silent rather than
  // claiming everything follows.
  if (!row || !row.masked.length) return null;
  const units = new Set(row.masked.map((m) => m.unitId));

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ py: 0.5 }}>
          {row.masked.map((m, i) => (
            <Typography
              variant="meta"
              key={`${m.unitId}-${m.by}-${i}`}
              sx={{ lineHeight: 1.6 }}
            >
              <b>{t("common:labels.unitNumber", { number: m.unitName })}</b>{" "}
              {m.by === "unit"
                ? t("signageGroupsPage.maskedDirect")
                : t("signageGroupsPage.maskedFollows", {
                    group: m.byGroupName ?? t("signageGroupsPage.anotherGroup"),
                  })}
              {REASON_SEPARATOR}
              {m.fields
                .map((f) =>
                  f in FIELD_KEYS
                    ? t(FIELD_KEYS[f as keyof typeof FIELD_KEYS])
                    : f
                )
                .join(", ")}
            </Typography>
          ))}
        </Box>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ cursor: "help", color: tokens.warning }}
      >
        <WarningAmberIcon sx={{ fontSize: 15 }} />
        <span style={{ fontSize: 12.5 }}>
          {t("signageGroupsPage.notFollowing", {
            count: units.size,
            screens: row.screens,
          })}
        </span>
      </Stack>
    </Tooltip>
  );
}

/**
 * Screen groups - a named set of screens, addressed as one thing.
 *
 * ## What the Type column is telling you
 *
 * Which LAYER a group sits on, and therefore who it belongs to. A location sees
 * its own groups, its chain's, and Nenda's - and can only edit its own. That is
 * the same rule as playlists and campaigns, and the column exists because a row
 * you cannot edit is otherwise indistinguishable from one you can until you
 * try.
 *
 * ## Sorting is client-side, deliberately
 *
 * A customer has tens of groups, not thousands. Paging and server-side sort
 * would be two more things to keep in agreement with the layer rules for a list
 * that fits in memory several times over.
 */

type SortKey = "name" | "createdAt" | "updatedAt" | "screens";

const SCOPE_KEYS = {
  premise: "signageGroupsPage.scopes.premise",
  company: "signageGroupsPage.scopes.company",
  global: "signageGroupsPage.scopes.global",
} as const;

export default function SignageGroupsPage({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const format = useFormat();
  // The column is a date; a time would only add noise.
  const day = (iso: string) => format.date(iso);
  const scopeLabel = (key: string) =>
    key in SCOPE_KEYS ? t(SCOPE_KEYS[key as keyof typeof SCOPE_KEYS]) : key;
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";

  const [groups, setGroups] = useState<SignageScreenGroup[]>([]);
  /**
   * Per group, which of its screens are ignoring it.
   *
   * Keyed by group id and allowed to arrive late - the table renders without it
   * and grows the warning when it lands. A masking check that has not answered
   * yet must never look like "nothing is masked", which is why this starts as
   * an empty MAP rather than an empty result per group.
   */
  const [following, setFollowing] = useState<
    Map<string, SignageGroupFollowing>
  >(new Map());
  const [screens, setScreens] = useState<GroupPickerUnit[]>([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("updatedAt");
  const [tick, setTick] = useState(0);

  const [editing, setEditing] = useState<SignageScreenGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<SignageScreenGroup | null>(null);
  /** The group whose CONTENT is being set, and what it currently says. */
  const [contentFor, setContentFor] = useState<SignageScreenGroup | null>(null);
  /**
   * Whether that drawer is open. Separate from `contentFor`, which is kept
   * while the drawer slides out so its title does not blank mid-animation.
   */
  const [contentOpen, setContentOpen] = useState(false);
  const [contentNow, setContentNow] = useState<SignageAssignment | null>(null);
  /** The group being taken over right now, or null. */
  const [takeoverFor, setTakeoverFor] = useState<SignageScreenGroup | null>(
    null
  );
  const [playlists, setPlaylists] = useState<SignagePlaylist[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<{
    el: HTMLElement;
    group: SignageScreenGroup;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignageGroupsIn(scope)
      .then((list) => alive && setGroups(list))
      .catch(() => alive && setGroups([]));
    foyerApi
      .listSignageGroupFollowingIn(scope)
      .then(
        (rows) =>
          alive && setFollowing(new Map(rows.map((r) => [r.groupId, r])))
      )
      // Silently: this is an annotation on a table that is already useful, and
      // an error banner over a working page would be the bigger failure.
      .catch(() => alive && setFollowing(new Map()));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /*
   * The screens the picker can offer, from whichever scope is open.
   *
   * A location lists its own; a customer lists every screen it has, across its
   * locations and any sub-companies. The second is the whole point of a
   * company-layer group - "every reception in the chain" cannot be built from
   * one hotel's list.
   *
   * The location LABEL is only filled in at company scope. Inside one hotel it
   * would be the same word on every row, and the dialog hides its location
   * filter when there is nothing to filter by.
   */
  useEffect(() => {
    let alive = true;
    // UNITS (UNIT-3): a group holds the spots, whatever screen hangs there.
    const load = foyerApi
      .listSignageUnitsIn(
        premiseId
          ? { kind: "premise", premiseId }
          : { kind: "company", companyId: companyId ?? "" }
      )
      .then((list) =>
        list.map(
          (u): GroupPickerUnit => ({
            id: u.id,
            label: t("common:labels.unitNumber", { number: u.name }),
            code: u.screens.length
              ? u.screens.map((s) => s.model ?? s.id).join(", ")
              : t("common:labels.noScreenYet"),
            premiseLabel: premiseId ? "" : u.premiseName,
            orientation: u.orientation,
          })
        )
      );
    load
      .then((rows) => alive && setScreens(rows))
      .catch(() => alive && setScreens([]));
    return () => {
      alive = false;
    };
  }, [premiseId, companyId, tick, t]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn(scope)
      .then((list) => alive && setPlaylists(list))
      .catch(() => alive && setPlaylists([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /*
   * Opened per group rather than loaded with the list: most groups are never
   * given content, and one request per row would be a request per row for a
   * page that is mostly read.
   */
  const openContent = async (g: SignageScreenGroup) => {
    setContentFor(g);
    setContentOpen(true);
    setContentNow(null);
    setError(null);
    try {
      setContentNow(await foyerApi.getSignageGroupAssignmentIn(scope, g.id));
    } catch {
      setError(t("signageGroupsPage.readFailed"));
    }
  };

  /** Resolves true on success; the drawer then slides out and closes. */
  const saveContent = async (
    patch: SignageAssignmentPatch
  ): Promise<boolean> => {
    if (!contentFor) return false;
    setBusy(true);
    setError(null);
    try {
      await foyerApi.putSignageGroupAssignmentIn(scope, contentFor.id, patch);
      setTick((t) => t + 1);
      return true;
    } catch {
      setError(t("signageGroupsPage.saveContentFailed"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /*
   * Force a channel onto every screen in the group, now.
   *
   * The same override a single screen gets, written one layer up. The resolver
   * already prefers a screen's OWN override to a group's, so a wall somebody
   * took over by hand this morning keeps what they chose - which is the rule
   * everywhere else and would be surprising if it stopped applying here.
   */
  const takeOver = async (playlistId: string, until: string | null) => {
    if (!takeoverFor) return;
    await foyerApi.putSignageGroupAssignmentIn(scope, takeoverFor.id, {
      override: { playlistId, since: new Date().toISOString(), until },
    });
    setTakeoverFor(null);
    setTick((t) => t + 1);
  };

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? groups.filter((g) => g.name.toLowerCase().includes(q))
      : groups;
    return [...rows].sort((a, b) => {
      switch (sort) {
        case "name":
          return a.name.localeCompare(b.name);
        case "createdAt":
          return b.createdAt.localeCompare(a.createdAt);
        case "screens":
          return b.unitIds.length - a.unitIds.length;
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
  }, [groups, query, sort]);

  const canEdit = (g: SignageScreenGroup) =>
    premiseId ? g.scope === "premise" : g.scope === "company";

  /** Resolves true on success; the surface then slides out and closes. */
  const save = async (input: {
    name: string;
    unitIds: string[];
  }): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await foyerApi.updateSignageGroupIn(scope, editing.id, input);
      } else {
        await foyerApi.createSignageGroupIn(scope, input);
      }
      setTick((t) => t + 1);
      toast.success(
        editing
          ? t("signageGroupsPage.updated", { name: input.name })
          : t("signageGroupsPage.created", { name: input.name })
      );
      return true;
    } catch (e) {
      setError(signageSaveErrorText(t, e, t("signageGroupsPage.saveFailed")));
      toast.error(
        describeError(t, "saveSignageGroup", e, { name: input.name })
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await foyerApi.deleteSignageGroupIn(scope, deleting.id);
      setDeleting(null);
      setTick((t) => t + 1);
      toast.success(t("signageGroupsPage.deleted", { name: deleting.name }));
    } catch (e) {
      setError(t("signageGroupsPage.deleteFailed"));
      toast.error(
        describeError(t, "deleteSignageGroup", e, { name: deleting.name })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("signageGroupsPage.title")}
        subtitle={t("signageGroupsPage.subtitle")}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: "center" }}
      >
        <TextField
          size="small"
          placeholder={t("signageGroupsPage.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": t("signageGroupsPage.searchAria") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 360 }}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          inputProps={{ "aria-label": t("common:labels.sort") }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="updatedAt">
            {t("signageGroupsPage.sortRecentlyChanged")}
          </MenuItem>
          <MenuItem value="createdAt">
            {t("signageGroupsPage.sortRecentlyCreated")}
          </MenuItem>
          <MenuItem value="name">{t("common:labels.name")}</MenuItem>
          <MenuItem value="screens">
            {t("signageGroupsPage.sortMostScreens")}
          </MenuItem>
        </TextField>
        <PermissionGate resource="signage" level="edit">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setError(null);
              setDialogOpen(true);
            }}
            data-testid="new-group"
          >
            {t("signageGroupsPage.newGroup")}
          </Button>
        </PermissionGate>
      </Stack>

      {error && (
        <Typography variant="bodySm" sx={{ mb: 2, color: tokens.error }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        {shown.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography
              sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
            >
              {groups.length === 0
                ? t("signageGroupsPage.none")
                : t("signageGroupsPage.noMatch")}
            </Typography>
            {groups.length === 0 && (
              <Typography
                variant="bodySm"
                sx={{ color: tokens.inkMuted, mt: 0.5 }}
              >
                {t("signageGroupsPage.noneHint")}
              </Typography>
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("signageGroupsPage.columns.name")}</TableCell>
                <TableCell>{t("signageGroupsPage.columns.created")}</TableCell>
                <TableCell>{t("signageGroupsPage.columns.changed")}</TableCell>
                <TableCell>{t("signageGroupsPage.columns.type")}</TableCell>
                <TableCell>{t("signageGroupsPage.columns.units")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((g) => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ fontWeight: "fontWeightSemibold" }}>
                    {g.name}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {day(g.createdAt)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {day(g.updatedAt)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {scopeLabel(g.scope)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <TvOutlinedIcon
                        sx={{ fontSize: 18, color: tokens.inkMuted }}
                      />
                      <span>
                        {t("glossary:unitCount", { count: g.unitIds.length })}
                      </span>
                      {g.orientation && (
                        <Chip
                          size="small"
                          data-testid={`group-orientation-${g.id}`}
                          label={
                            g.orientation === "mixed"
                              ? t("common:labels.mixed")
                              : g.orientation === "portrait"
                                ? t("common:labels.portrait")
                                : t("common:labels.landscape")
                          }
                          sx={{
                            typography: "micro",
                            height: 20,
                            ...(g.orientation === "mixed"
                              ? { bgcolor: tokens.warningTint }
                              : { bgcolor: tokens.rowAlt }),
                          }}
                        />
                      )}
                    </Stack>
                    <FollowingNote row={following.get(g.id)} />
                  </TableCell>
                  <TableCell align="right">
                    <PermissionGate resource="signage" level="edit">
                      <IconButton
                        size="small"
                        aria-label={t("common:labels.actionsFor", {
                          name: g.name,
                        })}
                        onClick={(e) =>
                          setMenu({ el: e.currentTarget, group: g })
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mt: 2 }}>
        {t("signageGroupsPage.showing", {
          shown: shown.length,
          total: groups.length,
        })}
      </Typography>

      <Menu
        open={menu !== null}
        anchorEl={menu?.el}
        onClose={() => setMenu(null)}
      >
        {/*
          A borrowed group offers neither action rather than offering them and
          failing: the server answers 404 for a layer this scope cannot write,
          and a menu that leads to that is a worse explanation than a menu that
          says so by being empty.
        */}
        {menu && canEdit(menu.group)
          ? [
              <MenuItem
                key="takeover"
                onClick={() => {
                  setTakeoverFor(menu.group);
                  setMenu(null);
                }}
              >
                {t("signageGroupsPage.changeChannelNow")}
              </MenuItem>,
              <MenuItem
                key="content"
                onClick={() => {
                  void openContent(menu.group);
                  setMenu(null);
                }}
              >
                {t("signageGroupsPage.setWhatItPlays")}
              </MenuItem>,
              <MenuItem
                key="edit"
                onClick={() => {
                  setEditing(menu.group);
                  setError(null);
                  setDialogOpen(true);
                  setMenu(null);
                }}
              >
                {t("signageGroupsPage.editGroup")}
              </MenuItem>,
              <MenuItem
                key="delete"
                sx={{ color: tokens.error }}
                onClick={() => {
                  setDeleting(menu.group);
                  setMenu(null);
                }}
              >
                {t("signageGroupsPage.deleteGroup")}
              </MenuItem>,
            ]
          : [
              <MenuItem key="none" disabled>
                {menu
                  ? t("signageGroupsPage.ownedBy", {
                      owner: scopeLabel(menu.group.scope),
                    })
                  : ""}
              </MenuItem>,
            ]}
      </Menu>

      <SignageGroupDialog
        open={dialogOpen}
        editing={editing}
        screens={screens}
        groups={groups}
        saving={busy}
        error={error}
        // `editing` is NOT cleared here: it is kept while the drawer slides
        // out (clearing it would flip it into the create modal mid-slide), and
        // "New screen group" resets it instead.
        onClose={() => setDialogOpen(false)}
        onSave={save}
      />

      <SignageChangeChannelDialog
        open={takeoverFor !== null}
        targetName={takeoverFor?.name ?? ""}
        targetScreens={takeoverFor?.screenIds.length ?? 0}
        playlists={playlists}
        onClose={() => setTakeoverFor(null)}
        onPlayNow={takeOver}
        onSchedule={() => setTakeoverFor(null)}
      />

      <SignageGroupContentDialog
        open={contentOpen}
        group={contentFor}
        current={contentNow}
        playlists={playlists}
        saving={busy}
        error={error}
        onClose={() => setContentOpen(false)}
        onSave={saveContent}
      />

      <SignageGroupDeleteDialog
        group={deleting}
        busy={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
    </PageContainer>
  );
}
